import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  forceCollide,
  forceSimulation,
  forceX,
  forceY,
  type Simulation,
  type SimulationNodeDatum,
} from "d3-force";
import { MACRO_COLORS, MACRO_KCAL, type BubbleEntry } from "@/lib/foods";

interface Props {
  bubbles: BubbleEntry[];
  width: number;
  height: number;
  onRemove: (id: string) => void;
  /** Daily kcal goal. Bubble area is calibrated so that sum at goal ≈ bowl area. */
  goalKcal: number;
  /** <1 shrinks the collision radius so bubbles overlap (cramped, no whitespace). */
  compression?: number;
}

interface Node extends SimulationNodeDatum {
  id: string;
  r: number; // base radius (visual + base collision)
  macro: BubbleEntry["macro"];
  grams: number;
  kcal: number; // 반지름 산정에 쓴 칼로리 값 (변경 감지용)
  color?: string;
  textColor?: string;
  foodName: string;
}

/**
 * Radius such that sum of bubble areas at total kcal = goal equals
 * bowlArea * packingFactor (≈ circle packing efficiency in a rounded container).
 */
function radiusForKcal(kcal: number, bowlArea: number, goalKcal: number, maxR: number) {
  const PACKING = 0.78;
  const targetArea = bowlArea * PACKING;
  const area = (kcal / goalKcal) * targetArea;
  const r = Math.sqrt(Math.max(area, 0) / Math.PI);
  return Math.max(12, Math.min(maxR, r));
}

export function BubbleField({
  bubbles,
  width,
  height,
  onRemove,
  goalKcal,
  compression = 1,
}: Props) {
  const nodesRef = useRef<Map<string, Node>>(new Map());
  const simRef = useRef<Simulation<Node, undefined> | null>(null);
  const prevCountRef = useRef<number>(0);
  const [, setTick] = useState(0);

  const cx = width / 2;
  // Always sink to bottom — gravity strength softens when packed so removing
  // a bubble in a crowded bowl doesn't cause the whole pile to snap downward.
  const anchorY = height - 4;
  const yStrength = 0.18 * compression; // compression<1 → gentler gravity

  const bowlArea = width * height;
  const maxR = height * 0.45;

  // Initialize simulation once
  useEffect(() => {
    const sim = forceSimulation<Node>([])
      .alphaDecay(0.03)
      .velocityDecay(0.55)
      .force("x", forceX(cx).strength(0.05))
      .force("y", forceY(anchorY).strength(yStrength))
      .force(
        "collide",
        forceCollide<Node>((d) => (d.r + 2) * compression)
          .strength(1)
          .iterations(4),
      )
      .on("tick", () => {
        for (const n of nodesRef.current.values()) {
          const r = n.r + 1;
          if (n.x! < r) n.x = r;
          if (n.x! > width - r) n.x = width - r;
          if (n.y! < r) n.y = r;
          if (n.y! > height - r) n.y = height - r;
        }
        setTick((t) => (t + 1) % 1000000);
      });
    simRef.current = sim;
    return () => {
      sim.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update forces when params change
  useEffect(() => {
    const sim = simRef.current;
    if (!sim) return;
    sim
      .force("x", forceX(cx).strength(0.05))
      .force("y", forceY(anchorY).strength(yStrength))
      .force(
        "collide",
        forceCollide<Node>((d) => (d.r + 2) * compression)
          .strength(compression < 1 ? 0.95 : 1)
          .iterations(5),
      );
    sim.alpha(0.6).restart();
  }, [cx, anchorY, yStrength, compression]);

  // Sync nodes with bubbles prop
  useEffect(() => {
    const map = nodesRef.current;
    // 그릇엔 (sizeKcal ?? grams) > 0 만 렌더. kcal 모드는 sizeKcal, macro 모드는 grams 폴백.
    // 0칼로리 placeholder는 걸러져 슬롯 목록에만 남는다.
    const visible = bubbles.filter((b) => (b.sizeKcal ?? b.grams) > 0);
    const incomingIds = new Set(visible.map((b) => b.id));

    let changed = false;
    for (const id of Array.from(map.keys())) {
      if (!incomingIds.has(id)) {
        map.delete(id);
        changed = true;
      }
    }
    for (const b of visible) {
      const kcal = b.sizeKcal ?? b.grams * MACRO_KCAL[b.macro];
      const existing = map.get(b.id);
      if (!existing) {
        const r = radiusForKcal(kcal, bowlArea, goalKcal, maxR);
        map.set(b.id, {
          id: b.id,
          r,
          macro: b.macro,
          grams: b.grams,
          kcal,
          color: b.color,
          textColor: b.textColor,
          foodName: b.foodName,
          x: cx + (Math.random() - 0.5) * 20,
          y: Math.max(r + 4, 10 + Math.random() * 20),
          vx: 0,
          vy: 0,
        });
        changed = true;
      } else if (existing.kcal !== kcal) {
        existing.kcal = kcal;
        existing.grams = b.grams;
        existing.r = radiusForKcal(kcal, bowlArea, goalKcal, maxR);
        if (existing.foodName !== b.foodName) existing.foodName = b.foodName;
        if (existing.color !== b.color) existing.color = b.color;
        if (existing.textColor !== b.textColor) existing.textColor = b.textColor;
        changed = true;
      }
    }

    const sim = simRef.current;
    if (!sim || !changed) return;
    sim.nodes(Array.from(map.values()));
    const prev = prevCountRef.current;
    const curr = map.size;
    prevCountRef.current = curr;
    // Removal: gentle resettle, gentler still when packed. Addition: stronger kick.
    const isRemoval = curr < prev;
    const alpha = isRemoval ? Math.max(0.12, 0.3 * compression) : 0.22;
    sim.alpha(alpha).restart();
  }, [bubbles, cx, bowlArea, goalKcal, maxR, compression]);

  const nodes = Array.from(nodesRef.current.values());

  return (
    <div className="relative overflow-hidden" style={{ width, height }}>
      <AnimatePresence>
        {nodes.map((n, i) => {
          const color = n.color ?? MACRO_COLORS[n.macro];
          const r = n.r;
          const phase = (i * 0.37) % 1;
          const swayDur = 3.6 + (i % 5) * 0.4;
          const swayAmp = 3 + (n.r % 4);
          return (
            <motion.div
              key={n.id}
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0 }}
              transition={{ duration: 0.3 }}
              className="absolute"
              style={{
                width: r * 2,
                height: r * 2,
                left: (n.x ?? 0) - r,
                top: (n.y ?? 0) - r,
                willChange: "transform, left, top",
              }}
            >
              <motion.button
                onClick={() => onRemove(n.id)}
                animate={{
                  y: [0, -swayAmp, 0, swayAmp * 0.7, 0],
                  x: [0, swayAmp * 0.5, 0, -swayAmp * 0.5, 0],
                  rotate: [0, swayAmp * 0.3, 0, -swayAmp * 0.3, 0],
                }}
                transition={{
                  duration: swayDur,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: -phase * swayDur,
                }}
                className="w-full h-full flex flex-col items-center justify-center rounded-full text-center shadow-lg"
                style={{
                  background: `radial-gradient(circle at 30% 30%, ${color}ee, ${color}aa 60%, ${color}66)`,
                  boxShadow: `inset -6px -8px 14px ${color}55, 0 4px 10px ${color}44`,
                  border: `1px solid ${color}`,
                }}
                aria-label={`${n.foodName} 제거`}
              >
                {r >= 20 && (
                  <span
                    className="text-[13px] font-semibold leading-tight px-1 break-words max-w-full"
                    style={{
                      color: n.textColor ?? (n.macro === "carbs" ? "#333" : "#fff"),
                    }}
                  >
                    {n.foodName}
                  </span>
                )}
              </motion.button>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
